import axios from "axios";
import qs from "qs";
import { API_ROUTE, FAST_API_URL, ORIGIN_URL, REPORT_URL } from "../config/constants";
import { classifyApiError, getApiErrorMessage, withAppFailureMessage, withResolvedMessage } from "../config/apiErrors";
import { isUnexpectedKey } from "../config/errorMessages";
import { unSubscribeDevice } from "../config/firebase";
import { reportError } from "../apm";

/* -------------------------------- */
/* Axios Instances */
/* -------------------------------- */

export const fcaGateWay = axios.create({
    baseURL: API_ROUTE, // Ruby (Core gateway)
    timeout: 300000,
    withCredentials: true,
    headers: {
        "Access-Control-Allow-Origin": ORIGIN_URL
    },
    paramsSerializer: {
        serialize: params =>
            qs.stringify(params, {
                arrayFormat: "brackets",
                skipNulls: true,
                filter: filterFunc
            })
    }
});

export const fcaFastApi = axios.create({
    baseURL: FAST_API_URL, // FastAPI (auth owner)
    timeout: 300000,
    withCredentials: true,
    paramsSerializer: {
        serialize: params =>
            qs.stringify(params, {
                arrayFormat: "repeat",
                skipNulls: true,
                filter: filterFunc
            })
    }
});

export const fcaReportGateway = axios.create({
    baseURL: REPORT_URL, // Django
    timeout: 300000,
    withCredentials: true,
    paramsSerializer: {
        serialize: params =>
            qs.stringify(params, {
                arrayFormat: "brackets",
                skipNulls: true,
                filter: reportFilterFunc
            })
    }
});

export const restCountriesGateWay = axios.create({
    baseURL: "https://restcountries.eu",
    timeout: 20000
});

/* -------------------------------- */
/* Logout handling */
/* -------------------------------- */

const handleLogout = async () => {
    const userId = localStorage.getItem("userId");

    try {
        await fcaFastApi.post("/api/v1/auth/revoke_refresh", {
            device_id: "",
            user_id: userId,
            device: "web"
        });
    } catch (_) {}

    if (userId) {
        unSubscribeDevice(userId);
    }

    localStorage.clear();
    sessionStorage.clear();
    rememberRoute();

    window.location.href = "/";
};

const rememberRoute = () => {
    const baseRoute = window.location.pathname;
    if (
        baseRoute?.split("/")?.[1] === "project-capital-request" ||
        baseRoute?.split("/")?.[1] === "assetmanagement" ||
        baseRoute?.split("/")?.[4] === "qaqc" ||
        baseRoute?.split("/")?.[1] === "export-routing-link"
    ) {
        localStorage.setItem("prevRoute", baseRoute);
    }
};

const AUTH_EXCLUDED_PATHS = [
    "/api/v1/auth/login",
    "/api/v1/auth/refresh-token",
    "/api/v1/auth/revoke_refresh",
    "/api/v1/auth/logout",
    "/api/v1/auth/exchange-code",
    "/api/v1/auth/sso/authorize-url",
    // Presence heartbeat (FCA-6979). Excluded so a background timer can never
    // drive the refresh-or-logout path: a 401 here is discarded silently and
    // the user's own next request decides the session's fate exactly as before.
    "/api/v1/sessions/heartbeat"
];

const isAuthExcludedUrl = url => {
    try {
        const path = new URL(url, window.location.origin).pathname;
        return AUTH_EXCLUDED_PATHS.some(p => path.endsWith(p));
    } catch {
        return false;
    }
};

/* -------------------------------- */
/* Refresh Token Logic (FastAPI) */
/* -------------------------------- */
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve();
        }
    });
    failedQueue = [];
};

/**
 * Refresh the session once, however many callers ask at the same time.
 *
 * Extracted from the interceptor below so callers that are NOT one of the three gateways can join the
 * SAME single flight — specifically Otto's own axios clients, which talk to the filter-ai backend.
 * Otto's calls started returning 401 when that backend's auth gate was turned on (pen-test 2026-08-31);
 * without this they were the only requests in the app that couldn't recover from an expired token, so
 * the assistant would sit there dead until some other feature happened to trigger a refresh.
 *
 * Sharing the flight is the whole point: a private copy of this logic in Otto would let two refreshes
 * race, and a refresh that loses the race revokes the token the winner just minted.
 *
 * Resolves once the session is refreshed. Rejects — after logging the user out — when it can't be.
 */
export const refreshAuthSession = () => {
    if (isRefreshing) {
        return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
        });
    }

    isRefreshing = true;

    return fcaFastApi
        .post("/api/v1/auth/refresh-token", {
            user_id: localStorage.getItem("userId"),
            device: "web"
        })
        .then(() => {
            processQueue();
        })
        .catch(async refreshError => {
            processQueue(refreshError);
            await handleLogout();
            throw refreshError;
        })
        .finally(() => {
            isRefreshing = false;
        });
};

/* -------------------------------- */
/* Error normalization */
/* -------------------------------- */

/**
 * Attach a user-safe message to every rejected request and log it to Elasticsearch.
 *
 * Deliberately does not render anything: alerts stay at the call site, so this cannot
 * produce a second toast alongside the one the component already shows.
 */
const normalizeAndReject = error => {
    const { key } = classifyApiError(error);
    const friendly = getApiErrorMessage(error);

    /* Only genuine faults are reported. Expected outcomes the user can resolve — validation,
       permission, not-found, bad login — are normal traffic, not incidents; logging them
       would bury real problems and inflate Elasticsearch volume. */
    const ref = isUnexpectedKey(key)
        ? reportError(error, {
              kind: "api",
              url: error.config?.url,
              method: error.config?.method,
              status: error.response?.status ?? null,
              code: error.code ?? null,
              friendly
          })
        : null;

    error.friendlyMessage = friendly;
    error.errorKind = key;
    error.errorRef = ref;

    if (!error.response) {
        // Timeouts and network failures have no response, so the long-standing
        // `e.response && e.response.data` pattern used across features yielded nothing and
        // the user saw no message at all. status 0 is a safe sentinel: existing code only
        // ever tests for specific statuses (406/415).
        error.response = { data: { message: friendly, detail: friendly, error: friendly, ref }, status: 0 };
    } else {
        const data = error.response.data;
        const isPlainObject = data && typeof data === "object" && !(typeof Blob !== "undefined" && data instanceof Blob);
        // Export flows read this as a Blob (see getExportErrorMessage) — leave those alone.
        error.response.data = isPlainObject ? withResolvedMessage(data, friendly, ref) : data;
    }

    return Promise.reject(error);
};

const attachAuthInterceptor = instance => {
    instance.interceptors.response.use(
        res => {
            /* A 200 carrying `success: false` is a failure the rejection path never sees, so give it
               the same message guarantee. Blobs (downloads) are left alone. */
            if (res?.data && typeof res.data === "object" && !(typeof Blob !== "undefined" && res.data instanceof Blob)) {
                res.data = withAppFailureMessage(res.data);
            }
            return res;
        },
        async error => {
            const originalRequest = error.config;

            if (error.response?.status !== 401 || originalRequest?._retry || isAuthExcludedUrl(originalRequest?.url)) {
                return normalizeAndReject(error);
            }

            /* A request that arrives mid-refresh waits for the in-flight one and then replays; it is
               deliberately NOT marked _retry, so it keeps the same second chance it always had. */
            if (isRefreshing) {
                return refreshAuthSession().then(() => instance(originalRequest));
            }

            originalRequest._retry = true;

            try {
                await refreshAuthSession();
                return instance(originalRequest);
            } catch (refreshError) {
                return Promise.reject(refreshError);
            }
        }
    );
};

attachAuthInterceptor(fcaGateWay);
attachAuthInterceptor(fcaReportGateway);
attachAuthInterceptor(fcaFastApi);

/* -------------------------------- */
/* Utils */
/* -------------------------------- */

function filterFunc(prefix, value) {
    const jsonKeys = [
        "list",
        "filter",
        "order",
        "filters",
        "search_filters",
        "top_n",
        "exclude_filters",
        "dynamic_filters",
        "jsonb_list",
        "jsonb_filters",
        "addon_filters",
        "jsonb_order",
        "hide_asset_columns",
        "field_order",
        "hide_recommendation_columns",
        "asset_filters",
        "jsonb_search_filters",
        "recommendation_filters",
        "priority_elements",
        "field_filter"
    ];
    return jsonKeys.includes(prefix) ? JSON.stringify(value) : value;
}

function reportFilterFunc(prefix, value) {
    if (
        prefix === "date_filter" ||
        prefix === "sort_order" ||
        prefix === "filters" ||
        (prefix === "filter" && typeof value !== "string") ||
        prefix === "core_report_filters" ||
        prefix === "image_ids"
    ) {
        return JSON.stringify(value);
    }
    return value;
}
